import {
	centsToDollars,
	numberToPercentage,
	numberToString,
	roundNumber,
} from '@marmon/shared/types/common';
import { AnalysisCostType, PeriodKey, TimePeriodProfit } from '../interfaces';
import { generateMargin } from './analysis.utils';

export function calculatePeriod(
	period: TimePeriodProfit,
	options: {
		totalOverheadInCents: number;
		totalRevenueInCents: number;
		totalOperatingIncomeInCents?: number;
		// forcing the value to be the overheadInCents regardless of the calculation
		forcedOverheadInCents?: number;
		totalTransactionCount?: number;
	},
): TimePeriodProfit {
	const { revenueInCents, costInCents, transactionCount } = period;
	const {
		totalOperatingIncomeInCents,
		totalRevenueInCents,
		forcedOverheadInCents,
		totalTransactionCount,
		totalOverheadInCents,
	} = options;

	const { valueInCents: marginInCents, percentage: marginPercent } =
		generateMargin(
			centsToDollars(revenueInCents),
			centsToDollars(costInCents),
		);

	let overheadPercent = 0.25;
	let overheadInCents = 0;

	if (forcedOverheadInCents) {
		overheadInCents = forcedOverheadInCents;
		overheadPercent = numberToPercentage(
			forcedOverheadInCents / totalOverheadInCents,
		);
	} else {
		// if we want to calculate based on transaction count, it will be proportional
		if (typeof totalTransactionCount !== 'undefined') {
			overheadPercent = numberToPercentage(
				transactionCount / totalTransactionCount,
			);
		} else {
			// isolating based on even quartile distribution
			overheadPercent = 0.25;
		}

		overheadInCents = roundNumber(
			options.totalOverheadInCents * overheadPercent,
			0,
		);
	}

	const operatingIncomeInCents = roundNumber(
		marginInCents - overheadInCents,
		0,
	);

	const revenuePercent = roundNumber(revenueInCents / totalRevenueInCents);
	const operatingIncomePercent =
		totalOperatingIncomeInCents != null
			? roundNumber(operatingIncomeInCents / totalOperatingIncomeInCents)
			: 1;

	const returnOnSalesPercent = roundNumber(
		operatingIncomeInCents / revenueInCents,
	);

	return {
		...period,
		revenuePercent,
		marginInCents,
		marginPercent,
		overheadPercent,
		overheadInCents,
		operatingIncomeInCents,
		operatingIncomePercent,
		returnOnSalesPercent,
	};
}

export const PeriodComparisonRows: PeriodKey[] = [
	'revenueInCents',
	'marginInCents',
	'marginPercent',
	'overheadInCents',
	'overheadPercent',
	'operatingIncomeInCents',
	'operatingIncomePercent',
	'returnOnSalesPercent',
] as const;

export const PeriodModelRows: PeriodKey[] = [
	'revenuePercent',
	'revenueInCents',
	'costInCents',
	'marginInCents',
	'marginPercent',
	'overheadInCents',
	'overheadPercent',
	'operatingIncomeInCents',
	'operatingIncomePercent',
	'returnOnSalesPercent',
] as const;

export const PeriodPercentageRows: PeriodKey[] = [
	'revenueInCents',
	'marginInCents',
	'marginPercent',
	'customerCount',
	'productCount',
] as const;

export const PeriodQuadOverviewRows: PeriodKey[] = [
	'revenueInCents',
	'revenuePercent',
	'marginInCents',
	'marginPercent',
	'productCount',
	'customerCount',
] as const;

export const PeriodFooterKeys: PeriodKey[] = [
	'operatingIncomeInCents',
	'operatingIncomePercent',
	'returnOnSalesPercent',
] as const;

export const PeriodValueTypeMap: {
	[key in PeriodKey]?: 'dollars' | 'percent' | 'count';
} = {
	revenuePercent: 'percent',
	revenueInCents: 'dollars',
	costInCents: 'dollars',
	marginInCents: 'dollars',
	marginPercent: 'percent',
	overheadInCents: 'dollars',
	overheadPercent: 'percent',
	operatingIncomeInCents: 'dollars',
	operatingIncomePercent: 'percent',
	returnOnSalesPercent: 'percent',
	customerCount: 'count',
	productCount: 'count',
};

export const PeriodValueTranslationMap = (
	costType: AnalysisCostType,
): {
	[key in PeriodKey]?: string;
} => ({
	marginInCents: `${costType}.marginInCents`,
	marginPercent: `${costType}.marginPercent`,
	costInCents: `${costType}.costInCents`,
});

export function PeriodValueFormatter(
	key: keyof TimePeriodProfit,
	value: number | null | undefined,
	options: {
		costType: AnalysisCostType;
	},
) {
	const { costType } = options;
	if (value == null) return '-';
	else if (costType === 'material' && PeriodFooterKeys.includes(key)) {
		return '-';
	}

	const valueType = PeriodValueTypeMap[key];
	if (valueType === 'percent') {
		return numberToString(numberToPercentage(value));
	} else if (valueType === 'dollars') {
		return numberToString(centsToDollars(value));
	}
	return numberToString(value);
}

export type TimePeriodTarget = 'total' | 'timePeriod1' | 'timePeriod2';
export type TimePeriodOption = {
	text: string;
	label: string;
	value: TimePeriodTarget;
};

export const TimePeriodOptions = (): {
	[key in TimePeriodTarget]: TimePeriodOption;
} => ({
	total: {
		label: 'profit.quad.phases.time-period-both',
		text: 'profit.quad.phases.time-period-both',
		value: 'total',
	},
	timePeriod1: {
		label: 'profit.quad.phases.time-period-1',
		text: 'profit.quad.phases.time-period-1',
		value: 'timePeriod1',
	},
	timePeriod2: {
		label: 'profit.quad.phases.time-period-2',
		text: 'profit.quad.phases.time-period-2',
		value: 'timePeriod2',
	},
});
